import { parseRuleBlocks, type RuleRecord } from "./ruleBlocksData";

export const ASSIGNMENT_PRIORITY_MIN = 0;
export const ASSIGNMENT_PRIORITY_MAX = 1000;
export const TARGET_TYPES = ["project", "client", "merchant"] as const;
export type TargetType = typeof TARGET_TYPES[number];
export interface ThresholdOverride { scope_id?: string; condition?: unknown; rule_threshold?: unknown; use_rule_threshold?: boolean; value?: string }
export interface Assignment { rule_id: number; target_type: TargetType; target_id: number; enabled: boolean; priority: number; rule_revision?: string; threshold_overrides?: ThresholdOverride[]; target_name?: string; rule_name?: string; rule_enabled?: boolean; rule?: RuleRecord }
export interface NewAssignmentModel { enabled: boolean; priority: number; rule_revision: string; threshold_overrides: ThresholdOverride[] }
export type AssignmentKey = Pick<Assignment, "rule_id" | "target_type" | "target_id">;
export type AssignmentFilter = Partial<AssignmentKey>;
export interface AssignmentPage { rows: Assignment[]; total: number | null; totalPages: number | null }
export const isRecord = (value: unknown): value is RuleRecord => !!value && typeof value === "object" && !Array.isArray(value);
export const isCustomThreshold = (row?: ThresholdOverride): boolean => !!row && row.use_rule_threshold !== true &&
	(row.use_rule_threshold === false || typeof row.value === "string" && row.value !== "");

export interface AssignmentThresholdEntry { row?: ThresholdOverride; conditionIndex: number | null; key: string }
export const assignmentThresholdEntries = (rule: RuleRecord, overrides: ThresholdOverride[]): AssignmentThresholdEntry[] => {
	const scopes = Array.isArray(rule.scope) ? rule.scope : [];
	const ids = scopes.map(scope => isRecord(scope) && typeof scope.scope_id === "string" ? scope.scope_id : undefined);
	const unmatchedIds = overrides.some(row => row.scope_id && !ids.includes(row.scope_id));
	const used = new Set<number>();
	const entries: AssignmentThresholdEntry[] = [];
	ids.forEach((id, index) => {
		let match = id ? overrides.findIndex((row, rowIndex) => !used.has(rowIndex) && row.scope_id === id) : -1;
		// Only legacy rows without IDs can be matched by position. Never guess an ID match.
		if (match < 0 && !id && overrides[index] && !overrides[index].scope_id && !used.has(index)) match = index;
		if (match >= 0) used.add(match);
		// Missing rule IDs prevent claiming these conditions have no local overrides.
		if (match < 0 && !id && unmatchedIds) return;
		entries.push({ row: match >= 0 ? overrides[match] : undefined, conditionIndex: index, key: `condition-${index}` });
	});
	overrides.forEach((row, index) => {
		if (!used.has(index)) entries.push({ row, conditionIndex: null, key: `unmatched-${index}` });
	});
	return entries;
};
const integer = (value: unknown): number | null => (typeof value === "number" || typeof value === "string" && /^-?\d+$/.test(value)) && Number.isSafeInteger(Number(value)) ? Number(value) : null;
const revision = (value: unknown) => typeof value === "string" && /^(?:[a-f0-9]{64})?$/.test(value);
const decimal = (value: unknown) => typeof value === "string" && /^[+-]?(?:\d{1,30}(?:\.\d{1,12})?|\.\d{1,12})$/.test(value);
export const parseThresholdOverrides = (value: unknown): ThresholdOverride[] | null => {
	if (!Array.isArray(value) || value.length > 4) return null;
	const rows = value.map(item => {
		if (!isRecord(item) || item.scope_id !== undefined && (typeof item.scope_id !== "string" || !/^[a-f0-9]{32}$/.test(item.scope_id)) || item.use_rule_threshold !== undefined && typeof item.use_rule_threshold !== "boolean" || item.value !== undefined && !(item.use_rule_threshold === true && item.value === "") && !decimal(item.value)) return null;
		return { ...(item.scope_id !== undefined ? { scope_id: item.scope_id as string } : {}),
			...(item.condition !== undefined ? { condition: item.condition } : {}),
			...(item.rule_threshold !== undefined ? { rule_threshold: item.rule_threshold } : {}),
			...(item.use_rule_threshold !== undefined ? { use_rule_threshold: item.use_rule_threshold } : {}),
			...(item.value !== undefined ? { value: item.value as string } : {}),
		};
	});
	return rows.some(row => !row) ? null : rows as ThresholdOverride[];
};
export const positiveId = (value: unknown): number | null => { const id = integer(value); return id !== null && id > 0 ? id : null; };
export const assignmentKey = (row: AssignmentKey) => `${row.rule_id}:${row.target_type}:${row.target_id}`;

export const unwrapAssignmentResponse = (response: unknown): unknown => {
	let data = response;
	for (let depth = 0; depth < 3 && isRecord(data); depth++) {
		if (data.error) throw data.error;
		if (!("data" in data)) break;
		data = data.data;
	}
	return data;
};
export const parseAssignment = (value: unknown): Assignment | null => {
	if (!isRecord(value)) return null;
	const rule_id = positiveId(value.rule_id), target_id = positiveId(value.target_id), priority = integer(value.priority);
	if (!rule_id || !target_id || !TARGET_TYPES.includes(value.target_type as TargetType) || typeof value.enabled !== "boolean" || priority === null || priority < ASSIGNMENT_PRIORITY_MIN || priority > ASSIGNMENT_PRIORITY_MAX) return null;
	const overrides = value.threshold_overrides === undefined ? undefined : parseThresholdOverrides(value.threshold_overrides);
	if (overrides === null || value.rule_revision !== undefined && !revision(value.rule_revision)) return null;
	return { rule_id, target_id, target_type: value.target_type as TargetType, enabled: value.enabled, priority,
		...(isRecord(value.rule) ? { rule: value.rule } : {}),
		...(typeof value.rule_revision === "string" ? { rule_revision: value.rule_revision } : {}),
		...(overrides !== undefined ? { threshold_overrides: overrides } : {}),
		...(typeof value.target_name === "string" ? { target_name: value.target_name } : {}),
		...(typeof value.rule_name === "string" ? { rule_name: value.rule_name } : {}),
		...(typeof value.rule_enabled === "boolean" ? { rule_enabled: value.rule_enabled } : {}),
	};
};
export const parseNewAssignmentModel = (value: unknown): NewAssignmentModel => {
	if (!isRecord(value) || typeof value.enabled !== "boolean") throw new Error("invalidResponse");
	const priority = integer(value.priority);
	const overrides = parseThresholdOverrides(value.threshold_overrides);
	if (priority === null || priority < ASSIGNMENT_PRIORITY_MIN || priority > ASSIGNMENT_PRIORITY_MAX || !revision(value.rule_revision) || !overrides) throw new Error("invalidResponse");
	return { enabled: value.enabled, priority, rule_revision: value.rule_revision as string, threshold_overrides: overrides };
};
export const assignmentProjectNames = (page: AssignmentPage, projects: readonly { id: number; name: string }[]): AssignmentPage => ({
	...page,
	rows: page.rows.map(row => {
		const name = row.target_type === "project" && !row.target_name ? projects.find(project => project.id === row.target_id)?.name : undefined;
		return name ? { ...row, target_name: name } : row;
	}),
});
export const parseAssignments = (response: unknown): AssignmentPage => {
	const data = unwrapAssignmentResponse(response);
	const page = parseRuleBlocks(data);
	if (!page) throw new Error("invalidResponse");
	const rows = page.rows.map(parseAssignment);
	if (rows.some(row => !row)) throw new Error("invalidResponse");
	return { ...page, rows: rows as Assignment[] };
};
export const assignmentRequest = (operation: "get" | "list" | "set" | "remove", projectId: number, data: Record<string, unknown>) => ({
	method: `rules/assignment/${operation}`, expectedProjectId: projectId, headers: { ignoreBusy: "true" }, data,
});
export const assignmentPayload = (ruleId: number, type: string, targetId: string, enabled: boolean, priority: string, model?: Pick<NewAssignmentModel, "rule_revision" | "threshold_overrides">): Assignment => {
	const result = parseAssignment({ rule_id: ruleId, target_type: type, target_id: targetId, enabled, priority, ...model });
	if (!result) throw new Error("invalidInput");
	return result;
};
export const assignmentHasNext = (page: AssignmentPage, index: number, size: number) => page.totalPages !== null ? index + 1 < page.totalPages : page.total !== null ? (index + 1) * size < page.total : page.rows.length >= size;

export const assignmentError = (error: unknown): string => {
	if (isRecord(error) && typeof error.message === "string") return [error.message, ...(Array.isArray(error.details) ? error.details.filter(item => typeof item === "string") : typeof error.details === "string" ? [error.details] : [])].filter(Boolean).join(" · ");
	return "requestError";
};
